import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useContext, useEffect, useMemo, useRef } from 'react';

import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';
import FormPanelContext from 'components/FormPanel/formPanelContext';

import {
  getDistrictAndStateByPincode,
  getStateOptionsWithNumericValue,
  getUniqueDistricts,
} from 'utils/pincodeDistrictState';
import { INDIAN_STATES } from 'utils/states';

const CVL_INVESTOR_APPLICATION_QUERY = gql(`
  query InvestorAccreditationApplicationForPersonalDetails($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      investmentEntityId
      firstName
      name
      addressLineOne
      addressLineTwo
      addressLineThree
      pincode
      city
      district
      state
      country
    }
  }
`);

export const ADD_PERSONAL_DETAILS_MUTATION = gql(`
  mutation AddPersonalDetails(
    $investmentEntityId: ID!
    $firstName: String!
    $middleName: String
    $lastName: String
    $addressLineOne: String!
    $addressLineTwo: String
    $addressLineThree: String
    $pincode: String!
    $city: String!
    $district: String!
    $state: String!
    $country: String!
  ) {
    addPersonalDetails(
      investmentEntityId: $investmentEntityId
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      addressLineOne: $addressLineOne
      addressLineTwo: $addressLineTwo
      addressLineThree: $addressLineThree
      pincode: $pincode
      city: $city
      district: $district
      state: $state
      country: $country
    ) {
      status
      pincode
    }
  }
`);

const STATE_OPTIONS = getStateOptionsWithNumericValue(INDIAN_STATES);

type Props = {
  selectedInvestmentEntity: any | null;
  onSuccess: () => void;
  onBack?: () => void;
  locked?: boolean;
};

const PincodeAutoFill: FC = () => {
  const { formState, formDispatch } = useContext(FormPanelContext);
  const previousPinRef = useRef<string | undefined>();

  useEffect(() => {
    const pin: string | undefined = formState?.data?.pin;
    if (!pin || pin === previousPinRef.current) return;

    previousPinRef.current = pin;

    const lookup = getDistrictAndStateByPincode(pin);
    if (!lookup) return;

    formDispatch({
      type: 'UPDATE_STATE',
      payload: { fieldName: 'district', value: lookup.district },
    });
    formDispatch({
      type: 'UPDATE_STATE',
      payload: { fieldName: 'stateCode', value: lookup.stateId },
    });
  }, [formState, formDispatch]);

  return null;
};

const PersonalDetails: FC<Props> = ({ selectedInvestmentEntity, onSuccess, onBack, locked }) => {
  const { data: cvlData, refetch: refetchCvlApplication } = useQuery(
    CVL_INVESTOR_APPLICATION_QUERY,
    {
      variables: { investmentEntityId: selectedInvestmentEntity?.id },
      skip: !selectedInvestmentEntity?.id,
      fetchPolicy: 'network-only',
    }
  );

  const cvlApplication = cvlData?.investorAccreditationApplication;
  const hasMatchingCvlApplication =
    !!cvlApplication &&
    String(cvlApplication.investmentEntityId) === String(selectedInvestmentEntity?.id);
  const useCvlData = hasMatchingCvlApplication && !!cvlApplication?.pincode;

  const [addPersonalDetails, { loading, error }] = useMutation(ADD_PERSONAL_DETAILS_MUTATION);

  const handleSubmit = async (data: any) => {
    if (!selectedInvestmentEntity?.id) {
      // This should not happen as an investment entity is selected before this step
      // but we guard against it to avoid calling the mutation with an undefined ID.
      // eslint-disable-next-line no-console
      console.error('No investment entity selected for adding personal details');
      return;
    }

    try {
      const result = await addPersonalDetails({
        variables: {
          investmentEntityId: selectedInvestmentEntity.id,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          addressLineOne: data.addressLine1,
          addressLineTwo: data.addressLine2,
          addressLineThree: data.addressLine3,
          pincode: data.pin,
          city: data.city,
          district: data.district,
          state: data.stateCode,
          country: data.country,
        },
      });

      if (result.errors?.length) {
        return;
      }

      // Success: non-null payload; status may not be 0 after API changes.
      const payload = result.data?.addPersonalDetails;
      if (payload == null) {
        return;
      }

      try {
        await refetchCvlApplication();
      } catch {
        // Still advance; form may refresh on next visit
      }

      onSuccess();
    } catch {
      // Network errors surface via mutation `error`
    }
  };

  const baseAddress = selectedInvestmentEntity?.kyc?.address ?? {};
  const cvlAddressOnly =
    cvlApplication &&
    ({
      lineOne: cvlApplication.addressLineOne,
      lineTwo: cvlApplication.addressLineTwo,
      lineThree: cvlApplication.addressLineThree,
      city: cvlApplication.city,
      state: cvlApplication.state,
      country: cvlApplication.country,
      pincode: cvlApplication.pincode,
      district: cvlApplication.district,
    } as const);

  const address = useCvlData && cvlAddressOnly ? cvlAddressOnly : baseAddress;

  const entityNameParts = selectedInvestmentEntity?.name?.split(' ') ?? [];
  const entityFirstName = entityNameParts[0];
  const entityMiddleName =
    entityNameParts.length > 2 ? entityNameParts.slice(1, -1).join(' ') : undefined;
  const entityLastName =
    entityNameParts.length > 1 ? entityNameParts[entityNameParts.length - 1] : undefined;
  const firstName =
    useCvlData && cvlApplication
      ? cvlApplication.firstName ??
        (cvlApplication.name ? cvlApplication.name.split(' ')[0] : undefined) ??
        entityFirstName
      : entityFirstName ?? '';
  const middleName = entityMiddleName ?? '';
  const lastName = entityLastName ?? '';

  const pincodeLookup = useMemo(
    () => getDistrictAndStateByPincode(address?.pincode),
    [address?.pincode]
  );
  const districtOptions = useMemo(() => getUniqueDistricts(), []);

  return (
    <>
      {locked ? (
        <FormPanelWithReadMode
          key={`${selectedInvestmentEntity?.id ?? 'none'}-${
            useCvlData ? cvlApplication?.pincode ?? 'cvl' : 'entity'
          }`}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          buttonRef={null}
          submitButtonLabel="Next"
          disableEdit={true}
        >
          <PincodeAutoFill />
          <FormInput
            fieldName="firstName"
            label="First Name *"
            type="text"
            placeholder="Enter your first name"
            defaultValue={firstName ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="middleName"
            label="Middle Name (Optional)"
            type="text"
            placeholder="Enter your middle name"
            defaultValue={middleName ?? ''}
          />
          <FormInput
            fieldName="lastName"
            label="Last Name (Optional)"
            type="text"
            placeholder="Enter your last name"
            defaultValue={lastName ?? ''}
          />
          <FormInput
            fieldName="addressLine1"
            label="Address Line 1 *"
            type="text"
            placeholder="Enter address line 1"
            defaultValue={address?.lineOne ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="addressLine2"
            label="Address Line 2 (Optional)"
            type="text"
            placeholder="Enter address line 2"
            defaultValue={address?.lineTwo ?? ''}
          />
          <FormInput
            fieldName="addressLine3"
            label="Address Line 3 (Optional)"
            type="text"
            placeholder="Enter address line 3"
            defaultValue={''}
          />
          <FormInput
            fieldName="pin"
            label="PIN *"
            type="text"
            placeholder="Enter PIN"
            defaultValue={address?.pincode ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="city"
            label="City *"
            type="text"
            placeholder="Enter city"
            defaultValue={address?.city ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="district"
            label="District *"
            type="select"
            placeholder="Select district"
            defaultValue={pincodeLookup?.district ?? ''}
            options={districtOptions}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="stateCode"
            label="State *"
            type="select"
            placeholder="Select state"
            defaultValue={pincodeLookup?.stateId ?? address?.state ?? ''}
            options={STATE_OPTIONS}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="country"
            label="Country *"
            type="text"
            placeholder="Enter country"
            defaultValue={address?.country ?? ''}
            validators={{ required: true }}
          />
        </FormPanelWithReadMode>
      ) : (
        <FormPanel
          key={`${selectedInvestmentEntity?.id ?? 'none'}-${
            useCvlData ? cvlApplication?.pincode ?? 'cvl' : 'entity'
          }`}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          buttonRef={null}
          submitButtonLabel="Next"
        >
          <PincodeAutoFill />
          <FormInput
            fieldName="firstName"
            label="First Name *"
            type="text"
            placeholder="Enter your first name"
            defaultValue={firstName ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="middleName"
            label="Middle Name (Optional)"
            type="text"
            placeholder="Enter your middle name"
            defaultValue={middleName ?? ''}
          />
          <FormInput
            fieldName="lastName"
            label="Last Name (Optional)"
            type="text"
            placeholder="Enter your last name"
            defaultValue={lastName ?? ''}
          />
          <FormInput
            fieldName="addressLine1"
            label="Address Line 1 *"
            type="text"
            placeholder="Enter address line 1"
            defaultValue={address?.lineOne ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="addressLine2"
            label="Address Line 2 (Optional)"
            type="text"
            placeholder="Enter address line 2"
            defaultValue={address?.lineTwo ?? ''}
          />
          <FormInput
            fieldName="addressLine3"
            label="Address Line 3 (Optional)"
            type="text"
            placeholder="Enter address line 3"
            defaultValue={''}
          />
          <FormInput
            fieldName="pin"
            label="PIN *"
            type="text"
            placeholder="Enter PIN"
            defaultValue={address?.pincode ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="city"
            label="City *"
            type="text"
            placeholder="Enter city"
            defaultValue={address?.city ?? ''}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="district"
            label="District *"
            type="select"
            placeholder="Select district"
            defaultValue={pincodeLookup?.district ?? ''}
            options={districtOptions}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="stateCode"
            label="State *"
            type="select"
            placeholder="Select state"
            defaultValue={pincodeLookup?.stateId ?? address?.state ?? ''}
            options={STATE_OPTIONS}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="country"
            label="Country *"
            type="text"
            placeholder="Enter country"
            defaultValue={address?.country ?? ''}
            validators={{ required: true }}
          />
        </FormPanel>
      )}
    </>
  );
};

export default PersonalDetails;
